import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Mail, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getDisplaySize } from '../mockData';
import { useArtists, useGallery } from '../lib/usePublicData';
import { PaintingFrame } from '../components/PaintingFrame';
import { useT } from '../lib/i18n';

const Monogram = ({ name, size = 'large' }) => {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 3)
    .toUpperCase();
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-rich-green via-dark-green to-charcoal relative">
      <div className="absolute inset-4 border-2 border-gold/40"></div>
      <div className="absolute inset-6 border border-gold/20"></div>
      <span
        className={`${size === 'large' ? 'text-9xl' : 'text-6xl'} text-gold tracking-widest relative z-10`}
        style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.15em' }}
      >
        {initials}
      </span>
    </div>
  );
};

const ArtistDetail = () => {
  const { t } = useT();
  const { slug } = useParams();
  const { items: artists, loading: la } = useArtists();
  const { items: gallery, loading: lg } = useGallery();
  const artist = artists.find((a) => a.slug === slug);
  const works = artist ? gallery.filter((w) => (w.artist || '').trim() === artist.name.trim()) : [];
  const [lightbox, setLightbox] = useState(null);

  if (la || lg) {
    return <div className="min-h-screen bg-cream flex items-center justify-center text-dark-green">Loading…</div>;
  }
  if (!artist) {
    return <Navigate to="/artists" replace />;
  }

  return (
    <div className="min-h-screen bg-cream">
      {/* Header / Hero */}
      <section className="relative bg-ivory py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] deco-pattern"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <Link
            to="/artists"
            data-testid="back-to-artists"
            className="inline-flex items-center gap-2 text-dark-green hover:text-dark-crimson transition-colors mb-12 font-semibold"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            <ArrowLeft size={20} />
            {t('artists.title')}
          </Link>

          <div className="grid lg:grid-cols-3 gap-12 items-start">
            {/* Photo / Monogram */}
            <div className="lg:col-span-1">
              <div className="relative h-96 border-4 border-gold">
                {artist.photo ? (
                  <img src={artist.photo} alt={artist.name} className="w-full h-full object-cover" />
                ) : (
                  <Monogram name={artist.name} />
                )}
                <div className="absolute -bottom-4 -right-4 w-full h-full border-4 border-dark-crimson -z-10"></div>
              </div>
            </div>

            {/* Bio */}
            <div className="lg:col-span-2">
              <div className="w-3 h-3 rotate-45 bg-gold mb-6 border-2 border-dark-crimson"></div>
              <p className="text-gold text-sm font-bold tracking-[0.3em] mb-3">
                {artist.nationality?.toUpperCase()}
                {artist.based && ` · BASED IN ${artist.based.toUpperCase()}`}
              </p>
              <h1
                className="text-6xl font-bold text-dark-green mb-6"
                style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.05em' }}
              >
                {artist.name}
              </h1>
              <div className="h-1 w-32 bg-dark-crimson mb-8"></div>
              <p
                className="text-charcoal text-xl leading-relaxed mb-8"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {artist.bio}
              </p>
              <Link to="/contact">
                <Button
                  data-testid="enquire-artist"
                  className="bg-dark-crimson hover:bg-crimson text-cream px-10 py-6 rounded-none border-2 border-gold transition-all duration-300 hover:scale-105 font-semibold tracking-wider"
                  style={{ fontFamily: 'Cormorant Garamond, serif' }}
                >
                  <Mail className="mr-2" size={18} />
                  {t('artists.inquireArtist')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Works by Artist */}
      <section className="py-20 px-6 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="w-3 h-3 rotate-45 bg-gold mx-auto mb-6 border-2 border-dark-crimson"></div>
            <h2
              className="text-5xl font-bold text-dark-green mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              {t('artists.worksBy')} {artist.name}
            </h2>
            <div className="h-1 w-32 bg-dark-crimson mx-auto"></div>
          </div>

          {works.length === 0 ? (
            <p
              className="text-center text-charcoal/70 text-xl"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              No works currently available. Please check back soon.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {works.map((work) => (
                <Card
                  key={work.id}
                  data-testid={`work-${work.id}`}
                  className="bg-ivory border-2 border-gold/30 overflow-hidden group hover:border-dark-crimson transition-all duration-500 hover:shadow-2xl hover:-translate-y-2"
                >
                  <div
                    className="relative h-96 overflow-hidden bg-ivory flex items-center justify-center px-10 py-8 cursor-zoom-in"
                    onClick={() => setLightbox({ image: work.image, title: work.title, size: work.size })}
                    data-testid={`work-image-${work.id}`}
                  >
                    <img
                      src={work.image}
                      alt={work.title}
                      className={`max-w-full max-h-full object-contain transition-opacity duration-700 ${work.imageContext ? 'group-hover:opacity-0' : ''}`}
                    />
                    {work.imageContext && (
                      <img
                        src={work.imageContext}
                        alt={`${work.title} in gallery`}
                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                      />
                    )}
                  </div>
                  <CardContent className="p-8 bg-ivory">
                    <h3
                      className="text-2xl font-bold text-dark-green mb-2"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {work.title}
                    </h3>
                    {work.size && (
                      <p
                        className="text-charcoal/70 text-sm mb-4"
                        style={{ fontFamily: 'Cormorant Garamond, serif' }}
                      >
                        {work.size}
                      </p>
                    )}
                    <p
                      className="text-charcoal mb-6 text-sm leading-relaxed"
                      style={{ fontFamily: 'Cormorant Garamond, serif' }}
                    >
                      {work.description}
                    </p>
                    <div className="flex justify-between items-center pt-6 border-t-2 border-gold/30">
                      <span className="text-3xl font-bold text-gold">{work.price}</span>
                      <Link to={`/contact?subject=${encodeURIComponent(`${t('common.inquireSubject')} ${work.title} — ${work.artist}`)}`}>
                        <Button className="bg-dark-crimson hover:bg-crimson text-cream rounded-none transition-colors border border-gold px-6 py-3">
                          {t('common.inquire')}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox / image viewer */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out animate-in fade-in duration-200"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            data-testid="lightbox-close"
            aria-label="Close"
            className="absolute top-6 right-6 text-cream hover:text-gold transition-colors p-2"
          >
            <X size={32} />
          </button>
          <div className="max-w-[92vw] max-h-[88vh] flex flex-col items-center gap-4">
            <img
              src={lightbox.image}
              alt={lightbox.title}
              onClick={(e) => e.stopPropagation()}
              className="max-w-full max-h-[80vh] object-contain shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)]"
            />
            <div className="text-center text-cream" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              <div className="text-2xl tracking-wide" style={{ fontFamily: 'Playfair Display, serif' }}>
                {lightbox.title}
              </div>
              {lightbox.size && (
                <div className="text-gold/80 text-sm mt-1">{lightbox.size}</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtistDetail;
